import { useEffect } from "react";
import { List, X } from "@phosphor-icons/react";
import { createPortal } from "react-dom";

/**
 * MobileTopBar + MobileDrawer — barra superior + gaveta lateral para
 * mobile/tablet. NÃO substitui o sidebar desktop existente: complementa.
 *
 * Uso nos shells:
 *   const [open, setOpen] = useState(false);
 *   <MobileTopBar title="Admin Master" onMenu={() => setOpen(true)} />
 *   <MobileDrawer open={open} onClose={() => setOpen(false)}>
 *     mesmo nav do sidebar existente
 *   </MobileDrawer>
 *
 * Comportamento:
 *   - TopBar visível apenas < 768px (`md:hidden`).
 *   - Sidebar desktop continua com `hidden md:flex`.
 *   - Drawer usa portal em `document.body` pra escapar de qualquer
 *     `overflow: hidden` do shell.
 *   - ESC fecha o drawer; click no backdrop também.
 *   - Trava scroll do body enquanto aberto (padrão iOS-safe).
 */

export function MobileTopBar({ title, onMenu, right = null }) {
  return (
    <div
      className="md:hidden sticky top-0 z-40 h-14 bg-[#0A0A0B]/95 backdrop-blur border-b border-white/5 flex items-center justify-between px-4"
      data-testid="mobile-topbar"
    >
      <button
        onClick={onMenu}
        aria-label="Abrir menu"
        data-testid="mobile-menu-open"
        className="w-9 h-9 -ml-1.5 rounded-md flex items-center justify-center text-slate-300 hover:bg-white/5 active:bg-white/10 transition-colors"
      >
        <List size={20} weight="regular" />
      </button>
      <div className="font-heading text-[13px] font-semibold tracking-tight text-white truncate">
        {title}
      </div>
      <div className="w-9 h-9 flex items-center justify-end">
        {right}
      </div>
    </div>
  );
}

export function MobileDrawer({ open, onClose, children }) {
  // Bloqueia scroll do body enquanto aberto, e escuta ESC.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="md:hidden fixed inset-0 z-[60]"
      data-testid="mobile-drawer"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="mobile-drawer-backdrop"
      />
      {/* painel */}
      <aside
        className="absolute inset-y-0 left-0 w-[82%] max-w-[300px] bg-[#0A0A0B] border-r border-white/5 flex flex-col shadow-2xl animate-slide-in-left overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end p-2 border-b border-white/5">
          <button
            onClick={onClose}
            aria-label="Fechar menu"
            data-testid="mobile-drawer-close"
            className="w-9 h-9 rounded-md flex items-center justify-center text-slate-300 hover:bg-white/5 transition-colors"
          >
            <X size={18} weight="regular" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
